import { z } from 'zod';
import { DisplayName, PackageName, Platform, Slug, Ulid } from './common.js';
import { DEFAULT_PIECE_MODEL, ModelChoice, PieceModelStem } from './piece-models.js';
import { ACHIEVEMENT_ICON_MAX_BYTES, MAX_ACHIEVEMENTS_PER_GAME } from './play.js';
import { PUBLIC_CHANNEL_SLUG, StoreReview } from './store.js';
/** Apps, their store listing, and channels (plan §3, §5). */
/* --------------------------------------------------------------------------- the listing */
/**
 * The store page.
 *
 * Every field here is **declared** by the developer, exactly like `versionName` is. The only
 * thing ever read out of an artifact is an APK's package id, at finalize — so a screenshot is
 * a file someone uploaded and "1-4 players" is a claim, not a measurement.
 *
 * The vocabulary follows the game pages on board.fun (players, ages, genre, mode, "if you
 * love…"), because a tester's Board is the same device those pages sell, and a listing that
 * reads differently there and here is a listing nobody trusts.
 */
/**
 * What kind of game it is. A game may declare several — most do not fit one word, and the ones
 * that need three ("party", "trivia", "kids") are exactly the ones a single choice describes
 * worst.
 *
 * Deliberately not a closed taxonomy anybody has to agree with: it is a filing system for
 * shelves and filters, so it stays short enough to fit on a screen and grows when a word is
 * genuinely missing. Both clients render whatever they are handed rather than mapping these to
 * anything, so adding one here is not a client release (see `GENRE_LIMIT`).
 */
export const AppGenre = z.enum([
    'action',
    'adventure',
    'arcade',
    'board',
    'card',
    'casual',
    'creative',
    'deduction',
    'drawing',
    'educational',
    'fighting',
    'horror',
    'kids',
    'music',
    'party',
    'platformer',
    'puzzle',
    'racing',
    'rhythm',
    'roguelike',
    'rpg',
    'sandbox',
    'shooter',
    'simulation',
    'sports',
    'strategy',
    'survival',
    'trivia',
    'word',
    'other',
]);
/**
 * How many a game may claim.
 *
 * Three, because the point of a genre is to narrow: a game filed under six is filed under none,
 * and every shelf it appears on is one a browsing player trusts a little less.
 */
export const GENRE_LIMIT = 3;
/** How it is played. Multi-select: a game is often both solo and competitive. */
export const PlayMode = z.enum(['solo', 'co-op', 'competitive', 'party']);
export const AppListing = z.object({
    /** One line under the name. Board.fun calls this the game's hook. */
    tagline: z.string().nullable(),
    /**
     * In the developer's order, and the first one leads: it is the word shown where only one
     * fits, and the shelf the game is filed under first. An empty list is "not declared" —
     * there is no null here for the same reason `modes` has none.
     */
    genres: z.array(AppGenre),
    /** Lower bound of the player count. */
    playersMin: z.number().int().nullable(),
    /** Upper bound, or null for "and up" — board.fun's `1+` is `{min: 1, max: null}`. */
    playersMax: z.number().int().nullable(),
    /** Minimum age, as the number in "Ages 8+". */
    minAge: z.number().int().nullable(),
    playTimeMinutes: z.number().int().nullable(),
    modes: z.array(PlayMode),
    /** "If you love: Chess." A comparison lands faster than a genre does. */
    ifYouLove: z.string().nullable(),
});
export const Tagline = z.string().trim().max(140);
const Players = z.number().int().min(1).max(64);
/** The listing fields, as they arrive on a `PATCH`. Every one is clearable with `null`. */
const ListingPatch = {
    tagline: Tagline.nullable().optional(),
    // Deduped on arrival rather than rejected: a repeated word is a slip in a chip picker, not a
    // request the server has to refuse, and the same game on one shelf twice is the only thing it
    // could possibly mean.
    genres: z
        .array(AppGenre)
        .max(GENRE_LIMIT)
        .transform((list) => [...new Set(list)])
        .optional(),
    playersMin: Players.nullable().optional(),
    playersMax: Players.nullable().optional(),
    minAge: z.number().int().min(0).max(21).nullable().optional(),
    playTimeMinutes: z.number().int().min(1).max(1440).nullable().optional(),
    modes: z.array(PlayMode).max(4).optional(),
    ifYouLove: z.string().trim().max(140).nullable().optional(),
};
/* ------------------------------------------------------------------------------- media */
/**
 * Store artwork. `icon`, `tile` and `hero` are one-per-app and replace on upload; screenshots
 * and videos are ordered lists.
 *
 * **A tile and a hero are two different pictures, not one picture at two sizes.** The tile is
 * the card on a shelf — 300 × 168 px on a Board — so it carries the game's logo, legible at that
 * size, and its main character or object, large and central: key art. The hero is what the whole
 * screen stands on behind the copy, the store panel and the rail, so it carries no text at all
 * and nothing that has to be seen whole: a scene. One image cannot do both jobs, which is why a
 * logo-heavy key art made a busy backdrop and an atmospheric scene made a blank tile when a tile
 * was the hero, cropped (`docs/store-plan.md` §13).
 *
 * Each falls back on the other, then on the icon, so a game with only one of the two still has
 * something in both places: a card draws tile → hero → icon and a backdrop draws hero → tile →
 * icon. `backdropArtOf` below is that rule for the dashboard; `ArtUrls.kt` is the Board's copy.
 */
export const AppMediaKind = z.enum(['icon', 'tile', 'hero', 'screenshot', 'video']);
/**
 * Every kind an `app_media` row may be: the store's five above, and `achievement` — one
 * achievement's icon (`docs/achievements-plan.md` §5.4), which rides the same table and the same
 * three calls but is **not store artwork**. Writing it takes `play:write` rather than
 * `store:write`, and no store read lists one: every reader of a listing selects `AppMediaKind`'s
 * kinds by name, so an icon an agent drew for a trophy never turns up in a game's gallery, on a
 * shelf or in the admin console's checklist.
 *
 * `AppMediaKind` stays the store's list on purpose — it is what the dashboard's listing slots, the
 * playground's store tools and agentd's copy of the kinds are written against.
 */
export const MediaKind = z.enum([...AppMediaKind.options, 'achievement']);
/** The one kind that is not store artwork. */
export const ACHIEVEMENT_MEDIA_KIND = 'achievement';
export const IMAGE_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const VIDEO_CONTENT_TYPES = ['video/mp4', 'video/webm'];
/** An achievement icon: PNG or WebP, the two a layer's renderer and every profile can draw (§5.4). */
export const ACHIEVEMENT_ICON_CONTENT_TYPES = ['image/png', 'image/webp'];
/**
 * What each kind accepts, shared so the dashboard can reject a 300 MB "screenshot" before it
 * spends five minutes uploading it and the server can reject it again on arrival.
 */
export const APP_MEDIA_LIMITS = {
    icon: { maxCount: 1, maxBytes: 2 * 1024 * 1024, contentTypes: IMAGE_CONTENT_TYPES },
    // Smaller than a hero on purpose: a hero is fetched for the one game on screen, a tile for
    // every game on every shelf a Board scrolls past. 1280 × 720 is four times the size it is
    // drawn at and fits in a fraction of this.
    tile: { maxCount: 1, maxBytes: 4 * 1024 * 1024, contentTypes: IMAGE_CONTENT_TYPES },
    hero: { maxCount: 1, maxBytes: 12 * 1024 * 1024, contentTypes: IMAGE_CONTENT_TYPES },
    screenshot: { maxCount: 12, maxBytes: 12 * 1024 * 1024, contentTypes: IMAGE_CONTENT_TYPES },
    // 80 MB, not "as big as you like": store media is uploaded by a browser and therefore
    // through the Worker (see lib/storage.ts), whose request body caps out near 100 MB. A
    // minute of 1080p lands well inside this; a whole gameplay capture does not, and should not
    // be a store page anyway.
    video: { maxCount: 4, maxBytes: 80 * 1024 * 1024, contentTypes: VIDEO_CONTENT_TYPES },
    // One file per achievement, and a game has at most a hundred of those. Measured at finalize, which
    // no store kind is: square, at least 256 px a side, with 128 and 256 px renditions written beside it.
    achievement: {
        maxCount: MAX_ACHIEVEMENTS_PER_GAME,
        maxBytes: ACHIEVEMENT_ICON_MAX_BYTES,
        contentTypes: ACHIEVEMENT_ICON_CONTENT_TYPES,
    },
};
/** A kind that holds one picture and replaces it on upload, rather than adding to a list. */
export const isSingleMediaKind = (kind) => APP_MEDIA_LIMITS[kind].maxCount === 1;
/**
 * What is wrong with a picture's shape for its slot, as one sentence — or null.
 *
 * **Advice, never a refusal.** The server does not open an image and the size is the uploader's
 * word, so this cannot be a rule; it is what the dashboard shows under a slot and what the
 * playground's store tools tell the assistant after an upload, in the same words. The case it
 * exists for is the common one: an image generator hands back 1024 × 1024 or 1536 × 1024, and a
 * Board crops every tile and hero to 16:9 — which takes the top of a logo with it.
 */
export function mediaShapeAdvice(kind, width, height) {
    if (!width || !height)
        return null;
    const size = `${width} × ${height}`;
    if (kind === 'icon') {
        return Math.abs(width / height - 1) > 0.02
            ? `This icon is ${size}, not square. A Board crops an icon to a square, so its sides are lost.`
            : null;
    }
    if (kind !== 'tile' && kind !== 'hero')
        return null;
    const fits = kind === 'tile' ? '1280 × 720' : '1920 × 1080';
    if (Math.abs(width / height / (16 / 9) - 1) > 0.03) {
        return `This ${kind} is ${size}, not 16:9. A Board crops it to 16:9, so ${kind === 'tile' ? 'a logo near the top or bottom edge is cut' : 'its edges are lost'}; ${fits} fits exactly.`;
    }
    // Where each is drawn: a tile at 300 × 168 on a shelf, a hero full screen. Below that it is
    // scaled up and goes soft — for a hero, well before it gets that small.
    if (kind === 'tile' ? width < 300 : width < 1280) {
        return `This ${kind} is ${size}, and a Board draws it ${kind === 'tile' ? 'at 300 × 168' : 'full screen at 1920 × 1080'}, so it is scaled up and looks soft; ${fits} is the size to upload.`;
    }
    return null;
}
/** What a page stands on: the hero, else the tile. The icon, which is not in `media` on every
 *  shape that carries one, is the caller's last resort. */
export function backdropArtOf(media) {
    return media.find((item) => item.kind === 'hero') ?? media.find((item) => item.kind === 'tile') ?? null;
}
/** What a card shows: the tile, else the hero. The other half of the rule above, for the one web
 *  surface that draws a game as a card — a share link's preview on a social network. */
export function cardArtOf(media) {
    return media.find((item) => item.kind === 'tile') ?? media.find((item) => item.kind === 'hero') ?? null;
}
export const AppMedia = z.object({
    id: Ulid,
    kind: AppMediaKind,
    /**
     * Public, unguessable and immutable: the bytes behind a media id never change, so a client
     * may cache it forever. Replacing an image creates a new id.
     */
    url: z.string(),
    contentType: z.string(),
    size: z.number().int(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    caption: z.string().nullable(),
    position: z.number().int(),
    createdAt: z.number().int(),
});
export const CreateAppMediaRequest = z.object({
    kind: MediaKind,
    contentType: z.string().trim().toLowerCase(),
    size: z.number().int().positive(),
    /**
     * Known for images the browser has already decoded; the server never measures store art. An
     * achievement icon's are measured at finalize, and what was declared here is replaced.
     */
    width: z.number().int().positive().max(20000).nullable().optional(),
    height: z.number().int().positive().max(20000).nullable().optional(),
    caption: z.string().trim().max(200).nullable().optional(),
    /**
     * The file's digest, lowercase hex. **Required for an achievement icon**, whose finalize checks
     * the bytes against it and which every pusher finds again by it, so an unchanged icon is never
     * sent twice (§5.4). Ignored for store art, whose bytes the server never reads.
     */
    sha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/, 'A sha256 is 64 lowercase hex characters.')
        .optional(),
});
export const CreateAppMediaResponse = z.object({
    mediaId: Ulid,
    uploadUrl: z.string(),
    method: z.literal('PUT'),
    headers: z.record(z.string(), z.string()),
    expiresAt: z.number().int(),
});
export const UpdateAppMediaRequest = z
    .object({
    caption: z.string().trim().max(200).nullable().optional(),
    /** Zero-based. The server renumbers the rest of the kind around it. */
    position: z.number().int().min(0).max(100).optional(),
})
    .refine((v) => Object.keys(v).length > 0, 'No fields to update.');
/* --------------------------------------------------------------------------------- apps */
export const CreateAppRequest = z.object({
    name: DisplayName,
    slug: Slug.optional(),
    platform: Platform.default('unity'),
    /**
     * Must be covered by one of the team's claimed prefixes — this is the check that makes
     * "can this team publish this package id?" answerable without ever opening an APK.
     */
    packageName: PackageName,
    description: z.string().trim().max(4000).optional(),
    /**
     * The Board Piece Model the game plays with: the game's primary and, at first, its whole list
     * (`apps.primary_model`, `apps.piece_models`, migration 0063).
     *
     * Asked at creation because it is a decision about the game rather than about a build: a team
     * whose game is not an Arcade game used to get `arcade` anyway and had to find the Models tab
     * to say otherwise. Optional on the wire, so a caller that predates the question gets exactly
     * what it always got. The shape is checked and the vocabulary is not, as on every other write
     * path that takes a stem (`PieceModelStem`).
     *
     * A starting value, not a lock: the game's Settings (and the playground picker, which is the same
     * control) change the list afterwards, for every channel at once.
     */
    primaryModel: PieceModelStem.default(DEFAULT_PIECE_MODEL),
});
export const UpdateAppRequest = z
    .object({
    name: DisplayName.optional(),
    /**
     * The app id — the short handle `boardflight --app <id>` and every CI script use.
     *
     * Renameable, and unique only within the team. It is derived from the name at creation
     * and has nothing to do with `packageName`: changing it does not touch what the app
     * installs as on a Board, and cannot, because the package id is the §4 publishing
     * boundary and is not patchable at all. What it *does* break is any script still passing
     * the old one, so the dashboard says so before it saves.
     */
    slug: Slug.optional(),
    description: z.string().trim().max(4000).nullable().optional(),
    ...ListingPatch,
    archived: z.boolean().optional(),
    /** Per-app retention override in days; null falls back to the instance default. */
    retentionDays: z.number().int().min(1).max(3650).nullable().optional(),
    /** Minimum retained builds per channel, regardless of age. */
    retentionMinBuilds: z.number().int().min(1).max(1000).nullable().optional(),
    /** Queue a hardware check as soon as a build is published (plan §15). */
    verifyOnPublish: z.boolean().optional(),
    /** Keep unverified builds out of testers' libraries entirely. */
    holdUntilVerified: z.boolean().optional(),
    /**
     * The Board Piece Models this game plays with, **in priority order** (P3 Step 0, P4).
     *
     * One list for the whole game: every channel offers the models on it that it holds a build
     * for, and its model choice (`Channel.modelChoice`) decides which of those a Board installs. The
     * first is priority 1 — the model a build that declared nothing is taken to be. Sent alone, the
     * order is the whole answer and the server stores `primaryModel` as its first entry. When priority
     * 1 changes, the game's builds that declared nothing are stamped with the old one first, so no
     * published build changes which pieces it is for.
     *
     * Not store presentation: it decides which bytes a Board downloads, so an API token is refused it
     * with every other such field, and no agent can change it.
     */
    pieceModels: z.array(PieceModelStem).min(1).max(64).optional(),
    /**
     * Priority 1, said separately — what a dashboard tab or a sandbox from before P4 sends beside the
     * list. Moved to the top of the list it names; refused with `primary_model_unsupported` when it
     * is not on it. Absent means the list's own first entry.
     */
    primaryModel: PieceModelStem.optional(),
})
    .refine((v) => Object.keys(v).length > 0, 'No fields to update.')
    // Only checkable when both arrive together; a patch that moves one of them past the other
    // is caught by the server, which re-checks against the stored row.
    .refine((v) => v.playersMin == null || v.playersMax == null || v.playersMin <= v.playersMax, { message: 'The smallest player count cannot be above the largest.', path: ['playersMax'] });
/**
 * The fields of that patch that describe the game, as opposed to the ones that decide how a team
 * ships it.
 *
 * `PATCH /v1/apps/:appId` carries both, and one of its two callers may only have the first half.
 * A person editing the dashboard's Details panel sends these; an API token holding `store:write`
 * — the playground assistant's, today — may send **these and nothing else**, and the route
 * refuses the rest by name (`docs/store-plan.md` §12).
 *
 * The line is drawn at consequence, not at sensitivity. Everything here is words and numbers a
 * store page shows, and the worst a wrong one does is embarrass. Everything left out changes what
 * happens to builds and testers: `slug` breaks every script that passes it, `archived` takes the
 * game away from the people working on it, and `verifyOnPublish`/`holdUntilVerified` are the two
 * switches that decide whether an unverified build reaches somebody's Board.
 *
 * It is a list rather than a second schema because there must be exactly one definition of what a
 * patch may contain: a `StoreListingPatch` parsed separately would drift from `UpdateAppRequest`
 * the first time a field was added to one of them.
 */
export const STORE_PRESENTATION_FIELDS = [
    'name',
    'description',
    'tagline',
    'genres',
    'playersMin',
    'playersMax',
    'minAge',
    'playTimeMinutes',
    'modes',
    'ifYouLove',
];
/** Which keys of a parsed patch are not part of the store presentation. In arrival order. */
export function nonPresentationFields(patch) {
    return Object.keys(patch).filter((key) => !STORE_PRESENTATION_FIELDS.includes(key));
}
/**
 * What is on a channel right now: the newest build published to it that is `ready`.
 *
 * A summary and not a `Build`, deliberately. This exists so a list of channels can answer "what
 * would a tester installing from here get?" without being the build timeline in miniature — the
 * timeline lives on the channel's own page, and everything a row would need to render one (size,
 * hardware check, uploader, notes) is left out on purpose.
 *
 * Nothing here is read out of the artifact: the version name and version code are the uploader's
 * word, as they are everywhere else.
 */
export const ChannelBuild = z.object({
    id: Ulid,
    /** Monotonic per app, assigned at finalize — so a `ready` build always has one. */
    buildNumber: z.number().int().nullable(),
    versionName: z.string(),
    versionCode: z.number().int(),
    /**
     * The Board Piece Model this build declared, as a registry stem (`chop_chop`), or null when
     * nothing was declared. The uploader's word like everything else here — see
     * `CreateBuildRequest.pieceModel`. `.optional()` so an older client parses a response carrying
     * it, and a stem `PIECE_MODELS` has never heard of is a name to draw, not an error.
     */
    pieceModel: z.string().nullable().optional(),
    createdAt: z.number().int(),
});
/**
 * A channel's colour, as a name from a closed list rather than a hex.
 *
 * The developer picks which channel is which colour; nobody picks the colour itself. A free hex
 * field would let a team choose `#FFFEF1` — the page background — and the dot that was supposed
 * to make `beta` recognisable at a glance would vanish into it. Every value here maps to a token
 * the design system already ships, so a channel dot cannot be a colour the rest of the dashboard
 * has never heard of, and re-theming moves the channels with everything else.
 *
 * Null is the honest fourth state and the default: a channel nobody has coloured is *unmarked*,
 * not grey-by-decree. Both clients render a colour they do not recognise as unmarked, which is
 * what makes adding a name here safe without a client release.
 */
export const CHANNEL_COLORS = ['purple', 'indigo', 'green', 'yellow', 'red', 'navy'];
export const ChannelColor = z.enum(CHANNEL_COLORS);
export const Channel = z.object({
    id: Ulid,
    appId: Ulid,
    slug: z.string(),
    name: z.string(),
    isDefault: z.boolean(),
    /** What this channel is marked with in the dashboard, or null when nobody has marked it. */
    color: ChannelColor.nullable(),
    /**
     * Whether devices are told this channel exists (migration 0030).
     *
     * False takes it out of `GET /v1/me/library` and out of realtime auth, for every caller — a
     * team member's Board stops listing it on the same read a playtester's does. It takes it out of
     * *nothing else*: builds still upload, promote and patch here, grants and redeem codes that name
     * it are untouched, its analytics and feedback keep arriving, and every developer-facing read —
     * this one included — still returns it. A channel that has served its playtest can therefore be
     * put down without taking anybody's access away one at a time, and switched back on without
     * giving it back.
     *
     * Not on `LibraryChannel`, deliberately: the library carries only channels that are on, so a
     * field saying so on every one of them would be a constant.
     */
    enabled: z.boolean(),
    /**
     * Whether a device shows the Board Piece Model interstitial before launching a game from this
     * channel (migration 0036, `docs/model-intro-plan.md`).
     *
     * On for `public` at app creation and off for every other channel, because every other channel
     * is a playtest whose installers already know what the game needs. It gates only the screen: the
     * model itself is the build's declaration, so a channel with this on and nothing declared shows
     * nothing.
     */
    modelIntro: z.boolean(),
    /**
     * Whether a game running from this channel tells the player a newer build is ready (migration
     * 0042, `docs/feedback-plan.md` §14).
     *
     * On for a web game's `playground` and `public` at creation and off everywhere else —
     * `updateNoticeDefault` in `feedback.ts` is that rule. It is a reason to patch builds published
     * here, exactly as the feedback button is, so on a Unity game switching it on is switching on a
     * re-sign.
     */
    updateNotice: z.boolean(),
    /**
     * Which of the game's models a Board installs from this channel (P4, migration 0064):
     * `top` (top priority only), `auto` (the highest priority whose pieces the Board has) or `pick`
     * (the player chooses, and can change it later). See `MODEL_CHOICES`.
     *
     * Starts at `modelChoiceDefault(slug)`: `auto` on `public`, `pick` on `internal` and
     * `playground`, `top` everywhere else. `.default` so a dashboard parses an older server's answer.
     */
    modelChoice: ModelChoice.default('top'),
    /**
     * The model this channel is about by default — **derived**, not stored (P3 Step 0).
     *
     * The highest-priority model in the game's list that this channel holds a build for
     * (`channelOffering`). It is what the store describes when it has nothing better and what a
     * device installs with no inventory to match against. Kept on the wire, under its old name,
     * because every Board client released since P2 reads it.
     */
    primaryModel: z.string(),
    /**
     * The models this channel offers — **derived**: the game's models with a ready build here, in
     * priority order, or the game's priority 1 alone when nothing has shipped; under `top`, only the
     * first of them. Compatibility is judged against this list. To change it, change the game's list
     * (`App.pieceModels`), the channel's `modelChoice`, or publish a build.
     */
    supportedModels: z.array(z.string()),
    /**
     * The current `ready` build **per declared model** — what an installer resolves over.
     *
     * A build that declared nothing counts as the game's priority 1's. Ordered by the game's list,
     * then any build for a model the game has since dropped. Developer-facing reads show every one —
     * including, under `top`, builds the channel holds but does not offer; the device-facing reads
     * carry only what `supportedModels` names.
     *
     * `.default([])` because an older server returns no such field and a newer client must still
     * parse that response — the same leniency `LibraryChannel` gets on the Android side.
     */
    modelBuilds: z.array(ChannelBuild).default([]),
    /**
     * The newest `ready` build here, or null when nothing has been published to this channel yet.
     *
     * Ranked by version code rather than by time, because that is what "current" means to the
     * device: a Board updates when the code goes up, so the build with the highest code on the
     * channel is the one a tester ends up running whatever order they arrived in. It is the same
     * build the version-code check makes the next upload beat.
     *
     * **Kept beside `modelBuilds` rather than replaced by it**, and not a second source of truth:
     * it is the same row as that list's newest entry. It stays because it is what "current" means on
     * every screen that has ever drawn this channel, and because removing a field an older Android
     * client reads is the one wire change this repo cannot make.
     */
    latestBuild: ChannelBuild.nullable(),
    createdAt: z.number().int(),
});
export const CreateChannelRequest = z.object({
    /**
     * Anything but `public`.
     *
     * That slug is the store's, and its meaning is attached to the word (`docs/store-plan.md` §1):
     * a build on it is installable by anybody with an account. Every app already has one from
     * creation, so this refuses a request that could only ever be a mistake — and it refuses it in
     * the shared schema, so the dashboard, the CLI and both plugins fail before the round trip.
     * The server re-checks; a schema is convenience, not the boundary.
     *
     * `playground` is reserved in the same list and deliberately *not* refused here: it is created
     * through this very endpoint, once per app, by the playground's own first deploy.
     */
    slug: Slug.refine((slug) => slug !== PUBLIC_CHANNEL_SLUG, {
        message: 'The public channel is created with the game. Publish to the library to use it.',
    }),
    name: DisplayName.optional(),
    isDefault: z.boolean().default(false),
    color: ChannelColor.optional(),
});
/**
 * `PATCH /v1/apps/:appId/channels/:channelId` — what a team may change about its own channel.
 *
 * A channel's slug is what `--channel` takes and what every grant, code and build row names, and
 * its default flag is the thing two channels cannot both be; neither has ever been editable and
 * neither becomes editable here.
 *
 * The rule for what *is* editable: **a team may change how its own channel behaves, and the way
 * back is the same control.** Colour is a handle for the eye, `enabled` is read by the two device
 * queries, `modelIntro` decides one screen, `updateNotice` decides whether a running game says a
 * newer build is ready and `modelChoice` decides which of the game's models a Board installs. All
 * five are reversible by the team that set them.
 *
 * Absent means "leave it alone" for every field, so a request that mentions one cannot silently
 * change another, and `color: null` clears the colour rather than being a way to omit it.
 *
 * **`primaryModel` and `supportedModels` moved to the game** (P3 Step 0). They are still parsed so
 * that a caller sending them — an older dashboard tab, a sandbox on an older image — is told where
 * they went (`400 piece_models_moved`) rather than having them silently dropped.
 */
export const UpdateChannelRequest = z.object({
    color: ChannelColor.nullable().optional(),
    enabled: z.boolean().optional(),
    modelIntro: z.boolean().optional(),
    updateNotice: z.boolean().optional(),
    modelChoice: ModelChoice.optional(),
    primaryModel: PieceModelStem.optional(),
    supportedModels: z.array(PieceModelStem).min(1).max(64).optional(),
});
/**
 * The store, from the side of the team that owns the game (`docs/store-plan.md` §3.7).
 *
 * On the app payload rather than an endpoint of its own because it is two facts a developer
 * needs *while looking at the game*: whether it is on the store, and what is stopping it. The
 * checklist is computed by `storeReadiness`, the same function the publish route re-checks with,
 * so the dashboard can never offer a button the server is about to refuse.
 */
export const AppStore = z.object({
    /**
     * Null = the team has not asked to be on the store. Set by publishing — or by submitting, for a
     * game not yet approved — cleared by unpublishing, deletes nothing.
     */
    publishedAt: z.number().int().nullable(),
    /**
     * When BoardFlight approved this game for the store (`docs/store-review-plan.md`). Null until an
     * operator has, once; never cleared after. A game is on the store when this *and* `publishedAt`
     * are set — `storeStanding` turns the pair and `review` into the one word the dashboard needs.
     */
    approvedAt: z.number().int().nullable(),
    /** The latest submission, or null for a game never submitted (or approved before review existed). */
    review: StoreReview.nullable(),
    /**
     * What is still missing from the listing. Empty means ready — including for a game that is
     * already published, where it stays empty unless somebody removes an icon afterwards.
     *
     * Strings rather than an enum on the wire, so a client built before an item was added shows
     * the words it was handed rather than dropping a line off the checklist.
     */
    missing: z.array(z.string()),
    /**
     * When an operator excused this game from the checklist above (`docs/admin-plan.md` §D5.7).
     *
     * Null for almost every game. When it is set, `missing` is **unchanged** — the items really are
     * still absent and the team should be able to finish them — but publishing no longer refuses.
     *
     * On the wire, and shown, deliberately. It is not a plan (`docs/plans-and-features.md` §4 forbids
     * revealing one of those); it is something done *to* this game, and a publish button that
     * silently started working would be worse than one that says why it did.
     */
    readinessWaivedAt: z.number().int().nullable(),
    /**
     * The game's short link code (`SHARE_CODE_ALPHABET` in `store.ts`), or null for a game never
     * published or submitted. Given once and never changed, so the dashboard can show the link as
     * soon as the game is live and it is the same link after any rename, unpublish or republish.
     */
    shareCode: z.string().nullable(),
    /**
     * The numbers, **exact** — this is the one response that carries them, because it is the one
     * read by the team whose game they are about. Every catalog surface rounds (`StoreCard`).
     *
     * Null until the cron has written a row, which is until something has been played, viewed or
     * installed. Null and zero mean different things and the dashboard says something different
     * for each: "nothing has happened yet" is not "nobody plays it".
     *
     * They are what BoardFlight's own client saw and nothing more (`docs/store-plan.md` §5.1): a
     * game launched from Board's own launcher while the client is closed is invisible here, and a
     * Board behind a shared NAT that has never paired undercounts. The dashboard says so beside
     * them rather than leaving somebody to discover it.
     */
    stats: z
        .object({
        /** A trailing week, and the total since this game's first counted event. */
        plays7d: z.number().int(),
        playsAll: z.number().int(),
        installs7d: z.number().int(),
        installsAll: z.number().int(),
        /**
         * Views have no all-time column. The funnel's first stage is honestly a trailing week —
         * a page open is the cheapest thing in the system and keeping every one of them forever
         * would be a telemetry log, which §2 says this deliberately is not.
         */
        views7d: z.number().int(),
        viewsAnon7d: z.number().int(),
        viewsSigned7d: z.number().int(),
        updatedAt: z.number().int(),
    })
        .nullable(),
});
export const App = z.object({
    id: Ulid,
    teamId: Ulid,
    teamName: z.string(),
    slug: z.string(),
    name: z.string(),
    platform: Platform,
    packageName: z.string(),
    description: z.string().nullable(),
    iconUrl: z.string().nullable(),
    listing: AppListing,
    /** Icon, hero, screenshots and videos, ordered as the store page shows them. */
    media: z.array(AppMedia),
    archivedAt: z.number().int().nullable(),
    retentionDays: z.number().int().nullable(),
    retentionMinBuilds: z.number().int().nullable(),
    verifyOnPublish: z.boolean(),
    holdUntilVerified: z.boolean(),
    /**
     * This app has a playground attached (`docs/playground-plan.md`).
     *
     * Not a platform and not a mode — the app is an ordinary `web` app whose builds happen to be
     * made in a browser workspace, and every one of them still arrives through the ordinary
     * three-call upload. What it buys the dashboard is the one thing it cannot work out for
     * itself: that publishing here is a button in the playground rather than a form, so the
     * version, the version code and the release note are all settled by the deploy and there is
     * nothing on this page to fill in.
     */
    hasPlayground: z.boolean(),
    /** The store switch and its checklist. `.optional()` so an older client parses this response. */
    store: AppStore.optional(),
    /**
     * The Board Piece Models this game plays with, in priority order (P3 Step 0, P4; migration 0063).
     *
     * The one list: the game's Settings, the playground picker and the new-game form all write it,
     * and every channel's `supportedModels` is derived from it. `.default` so a client parses a
     * response from a server that predates it.
     */
    pieceModels: z.array(z.string()).default([]),
    /**
     * Priority 1 — `pieceModels[0]`, said on its own because clients from before P4 read it. The model
     * a build that declared nothing is taken to be, everywhere in this game.
     */
    primaryModel: z.string().default(DEFAULT_PIECE_MODEL),
    createdAt: z.number().int(),
    channels: z.array(Channel),
});
/* ------------------------------------------------------------- the games list's numbers */
/** How far back the games list's numbers reach: a trailing week ending now, not calendar days. */
export const GAMES_GLANCE_DAYS = 7;
/**
 * A few numbers per game, for the team's games list (`GET /v1/teams/:teamId/apps/glance`).
 *
 * Counts and nothing else: no device, no account, no name. A list that says "3 Boards" has no
 * business knowing whose.
 *
 * `boards` and `plays` are the trailing week. The two feedback counts are not: they are what is
 * waiting on the team now, however old (owner, 2026-10-20, `docs/tickets-plan.md` §8.7).
 *
 * Who reports the first two changed when BoardFlight's own app left the Board
 * (`docs/off-board-plan.md` §6), and so did what they mean:
 *
 * - `plays` are the store's play events (`docs/store-plan.md` §5): the starts a game reports about
 *   itself, one per accepted in-game session start. They are the Store page's "Plays" on the same
 *   rows, counted live where that page reads the hourly rollup, so the two can differ by the last
 *   hour. A game whose build carries no in-game layer — a Unity game that never switched play
 *   sessions on — has none.
 * - `boards` are real Boards: the ones a BoardFlight desktop app found the game on at a sync in the
 *   window, for a game with analytics on. A Board nobody's desktop app has read — every Board a
 *   game was put on by hand through the Board's own page — is not in it.
 */
export const GameGlance = z.object({
    appId: Ulid,
    /** Boards a desktop app found the game installed on in the window. Zero with analytics off. */
    boards: z.number().int(),
    /**
     * Starts the game reported, with repeats from one network address inside
     * `PLAY_EVENT_DEDUPE_MINUTES` counted once.
     */
    plays: z.number().int(),
    /**
     * Feedback in New now — what nobody on the team has accepted or turned down yet — whatever its
     * age. **Absent** — not zero — for a team without the `feedback.layer` feature, so the response
     * says nothing about a capability the team lacks; and so is the next one.
     */
    feedbackNew: z.number().int().optional(),
    /** Feedback accepted and not finished now: To Do, In Progress and Blocked (`TICKET_ACCEPTED_STATUSES`). */
    feedbackAccepted: z.number().int().optional(),
    /**
     * Achievements in the game's current play catalog, whatever anybody holds. **Absent** for a team
     * without `play.services`, as feedback is without its feature.
     */
    achievements: z.number().int().optional(),
    /** Custom events in the game's current event catalog. **Absent** for a team without `analytics.events`. */
    events: z.number().int().optional(),
});
export const GamesGlance = z.object({
    /** `GAMES_GLANCE_DAYS`, said on the wire so a client never has to assume the window. */
    days: z.number().int(),
    /**
     * Only the games with something to count. A game missing from the list had nothing in the
     * window and no feedback open, which the dashboard draws as a quiet dash rather than as a row of
     * zeros.
     */
    items: z.array(GameGlance),
});
//# sourceMappingURL=apps.js.map